import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeft, 
  Save, 
  CheckCircle, 
  Camera, 
  MapPin,
  Wifi,
  WifiOff,
  FileText
} from 'lucide-react';
import { inspectionsApi, templatesApi, mediaApi } from '../services/api';
import { useInspectionStore } from '../stores';
import { Question, Template } from '../types';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

const InspectionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { addOfflineInspection, updateOfflineInspection, removeOfflineInspection } = useInspectionStore();
  
  const isEditing = Boolean(id);
  const templateId = searchParams.get('template');
  
  const [template, setTemplate] = useState<Template | null>(null);
  const [inspectionId, setInspectionId] = useState<string | null>(id || null);
  const [offlineId] = useState(() => uuidv4());
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [gpsCoords, setGpsCoords] = useState<{lat: number, lng: number} | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [status, setStatus] = useState<'draft' | 'in_progress' | 'completed'>('draft');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [saving, setSaving] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activePhotoQuestion, setActivePhotoQuestion] = useState<string | null>(null);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get GPS location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => console.log('GPS not available')
      );
    }
  }, []);

  // Load template or existing inspection
  const { data: existingInspection } = useQuery(
    ['inspection', id],
    () => inspectionsApi.getById(id!).then(r => r.data),
    { enabled: isEditing }
  );

  const { data: templateData } = useQuery(
    ['template', templateId],
    () => templatesApi.getById(templateId!).then(r => r.data),
    { enabled: Boolean(templateId) && !isEditing }
  );

  useEffect(() => {
    if (existingInspection) {
      setTemplate(existingInspection.Template);
      setTitle(existingInspection.title);
      setLocation(existingInspection.location || '');
      setGpsCoords(existingInspection.gpsCoordinates);
      setResponses(existingInspection.responses || {});
      setStatus(existingInspection.status);
    } else if (templateData) {
      setTemplate(templateData);
      setTitle(templateData.title);
    }
  }, [existingInspection, templateData]);

  const saveMutation = useMutation(
    (data: any) => isEditing 
      ? inspectionsApi.update(id!, data)
      : inspectionsApi.create(data),
    {
      onSuccess: (response) => {
        if (!isEditing) {
          setInspectionId(response.data.id);
        }
        queryClient.invalidateQueries('inspections');
      }
    }
  );

  const handleSave = async (newStatus?: 'draft' | 'in_progress' | 'completed') => {
    setSaving(true);
    const saveStatus = newStatus || status;
    
    const inspectionData = {
      templateId: template?.id,
      title,
      location,
      gpsCoordinates: gpsCoords,
      responses,
      status: saveStatus,
      offlineId: isOnline ? undefined : offlineId
    };

    try {
      if (isOnline) {
        await saveMutation.mutateAsync(inspectionData);
        toast.success('Inspection saved');
        
        if (saveStatus === 'completed') {
          navigate('/inspections');
        }
      } else {
        // Save offline
        const offlineData = {
          ...inspectionData,
          offlineId,
          startedAt: new Date().toISOString(),
          syncStatus: 'pending'
        };
        
        if (!isEditing) {
          addOfflineInspection(offlineData);
        } else {
          updateOfflineInspection(offlineId, offlineData);
        }
        
        toast.success('Saved offline - will sync when online');
      }
      
      if (newStatus) {
        setStatus(newStatus);
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleResponse = (questionId: string, value: any) => {
    setResponses({ ...responses, [questionId]: { value, timestamp: new Date().toISOString() } });
  };

  const handlePhotoUpload = async (questionId: string, file: File) => {
    try {
      if (isOnline && inspectionId) {
        const response = await mediaApi.upload(file, {
          inspectionId,
          questionId,
          type: 'photo'
        });
        handleResponse(questionId, { photoUrl: response.data.url, mediaId: response.data.id });
      } else {
        // Store photo locally for offline
        const reader = new FileReader();
        reader.onload = (e) => {
          handleResponse(questionId, { 
            photoData: e.target?.result,
            offlinePhoto: true,
            questionId
          });
        };
        reader.readAsDataURL(file);
      }
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const calculateScore = () => {
    if (!template?.questions) return { score: 0, maxScore: 0 };
    
    let score = 0;
    let maxScore = 0;
    
    template.questions.forEach(q => {
      if (q.scoring?.enabled) {
        maxScore += q.scoring.weight || 1;
        const response = responses[q.id];
        if (response?.value === q.scoring.correctAnswer) {
          score += q.scoring.weight || 1;
        }
      }
    });
    
    return { score, maxScore };
  };

  const renderQuestion = (question: Question, index: number) => {
    const response = responses[question.id];
    
    return (
      <div key={question.id} className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-semibold">
            {index + 1}
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              {question.text}
              {question.required && <span className="text-red-500"> *</span>}
            </h3>
            {question.description && (
              <p className="text-sm text-gray-600 mt-1">{question.description}</p>
            )}

            <div className="mt-4">
              {question.type === 'text' && (
                <input
                  type="text"
                  value={response?.value || ''}
                  onChange={(e) => handleResponse(question.id, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="Enter your answer..."
                />
              )}

              {question.type === 'number' && (
                <input
                  type="number"
                  value={response?.value || ''}
                  onChange={(e) => handleResponse(question.id, parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="Enter a number..."
                />
              )}

              {question.type === 'yes_no' && (
                <div className="flex gap-4">
                  {['yes', 'no'].map((option) => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={response?.value === option}
                        onChange={() => handleResponse(question.id, option)}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="capitalize">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {question.options?.map((option, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(response?.value || []).includes(option)}
                        onChange={(e) => {
                          const current = response?.value || [];
                          const updated = e.target.checked
                            ? [...current, option]
                            : current.filter((v: string) => v !== option);
                          handleResponse(question.id, updated);
                        }}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'date' && (
                <input
                  type="date"
                  value={response?.value || ''}
                  onChange={(e) => handleResponse(question.id, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                />
              )}

              {question.type === 'rating' && (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => handleResponse(question.id, star)}
                      className={`text-2xl ${(response?.value || 0) >= star ? 'text-amber-400' : 'text-gray-300'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              )}

              {question.type === 'photo' && (
                <div className="space-y-3">
                  {response?.value?.photoUrl || response?.value?.photoData ? (
                    <div className="relative inline-block">
                      <img 
                        src={response.value.photoUrl || response.value.photoData} 
                        alt="Captured" 
                        className="w-48 h-48 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleResponse(question.id, null)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setActivePhotoQuestion(question.id);
                        fileInputRef.current?.click();
                      }}
                      className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                    >
                      <Camera className="w-5 h-5" />
                      <span>Add Photo</span>
                    </button>
                  )}
                </div>
              )}

              {question.type === 'signature' && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <p className="text-gray-500">Signature capture would appear here</p>
                  <p className="text-xs text-gray-400 mt-1">(Using react-signature-canvas in production)</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!template) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/inspections')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{title}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">{template.title}</span>
              {isOnline ? (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <Wifi className="w-3 h-3" /> Online
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <WifiOff className="w-3 h-3" /> Offline
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
          
          <button
            onClick={() => {
              const { score, maxScore } = calculateScore();
              handleSave('completed');
            }}
            disabled={saving}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            Complete
          </button>
        </div>
      </div>

      {/* Location Input */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>
          {gpsCoords && (
            <div className="text-xs text-gray-500">
              GPS: {gpsCoords.lat.toFixed(4)}, {gpsCoords.lng.toFixed(4)}
            </div>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {template.questions?.map((question, index) => renderQuestion(question, index))}
      </div>

      {/* Hidden file input for photos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && activePhotoQuestion) {
            handlePhotoUpload(activePhotoQuestion, file);
            setActivePhotoQuestion(null);
          }
        }}
      />

      {/* Bottom Actions */}
      <div className="mt-8 flex justify-center gap-4">
        <button
          onClick={() => handleSave('draft')}
          disabled={saving}
          className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Save Draft
        </button>
        <button
          onClick={() => handleSave('completed')}
          disabled={saving}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          Complete Inspection
        </button>
      </div>
    </div>
  );
};

export default InspectionForm;